import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Formik, Form, Field } from 'formik';
import { useMutation } from '@apollo/react-hooks';
import * as yup from 'yup';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import { ListBox } from 'primereact/listbox';
import { ScrollPanel } from 'primereact/scrollpanel';
import Debug from '../../../../components/Debug';
import { UPDATE_LOCAL_ARI } from '../mutations';
import messages from '../intl-messages';
import { withGrowl } from '../../../../components/GrowlNotifications';
import AnswerField from '../GlobalCreate/AnswerField';


const answerValidationSchema = formatMessage => yup.object({
  answer: yup.string()
    .max(50, formatMessage(messages.answerValidationMaxLength))
    .required(formatMessage(messages.answerValidationRequired)),
});

const AnswersList = ({ answers, setAnswers, formatMessage }) => {
  const answerTemplate = option => (
    <Button
      label={option.label}
      className=".p-button-info"
      style={{ width: '100%' }}
      onClick={() => {
        const newAnswers = _.filter(answers, answer => answer.value !== option.value);
        setAnswers(newAnswers);
      }}
    />
  );


  return (
    <div>
      <div>
        <small>
          {formatMessage(messages.answerListLabelMessage)}
        </small>
      </div>
      <ListBox
        disabled
        options={answers}
        itemTemplate={answerTemplate}
        style={{ width: '100%' }}
      />
    </div>
  );
};

AnswersList.propTypes = {
  answers: PropTypes.arrayOf(PropTypes.any).isRequired,
  setAnswers: PropTypes.func.isRequired,
  formatMessage: PropTypes.func.isRequired,
};

const answersChanged = (answersBefore, answersAfter) => !_.isEqual(answersBefore.sort(), answersAfter.sort());

const EditAnswersDialog = ({
  isVisible, onHide, field, currentAccount, intl: { formatMessage }, growl,
}) => {
  const [updateLocalAri] = useMutation(UPDATE_LOCAL_ARI, { refetchQueries: ['getAccount'] });
  const [answers, setAnswers] = useState(field.answers.map(a => ({
    label: a, value: a, key: a,
  })));
  const maxAnswers = 500;

  return (
    <Dialog
      header={`${formatMessage(messages.editDropdownAnswersDialogHeader)}: "${field.question}"`}
      visible={isVisible}
      style={{ width: '50vw' }}
      onHide={onHide}
    >
      <ScrollPanel style={{ width: '100%', height: '400px' }}>
        <div
          className="p-grid p-justify-center"
          style={{ marginTop: '10px' }}
        >
          <Formik
            validationSchema={answerValidationSchema(formatMessage)}
            initialValues={{ answer: '' }}
            onSubmit={(values, { resetForm }) => {
              const duplicateAnswer = _.includes(answers.map(ans => ans.value), values.answer);
              if (!duplicateAnswer) {
                setAnswers([...answers, {
                  label: values.answer, value: values.answer, key: values.answer,
                }]);
                resetForm();
              }
            }}
          >
            {({ resetForm }) => (
              <Form>
                {
                                    answers.length < maxAnswers && (
                                    <div className="p-col-12">
                                      <Field
                                        name="answer"
                                        component={AnswerField}
                                        placeholder={formatMessage(messages.answerFieldPlaceholder)}
                                      />
                                      <Button
                                        style={{ marginTop: '5px' }}
                                        type="submit"
                                        label={formatMessage(messages.addAnswerBtnLabel)}
                                        className="p-button-secondary"
                                      />
                                    </div>
                                    )
                                }
                {answers.length === 0 && (
                <div style={{ marginLeft: '10px' }}>
                  <small>
                    {formatMessage(messages.answerListEmptyMessage)}
                  </small>
                </div>
                )}
                {answers.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <AnswersList
                    answers={answers}
                    setAnswers={setAnswers}
                    formatMessage={formatMessage}
                  />
                </div>
                )}
                <div style={{
                  textAlign: 'center',
                  marginTop: '10px',
                  marginBottom: '10px',
                }}
                >
                  <Button
                    type="button"
                    label={formatMessage(messages.updateLabel)}
                    disabled={
                                            answers.length === 0
                                            || answers.length > maxAnswers
                                            || !answersChanged(field.answers, answers.map(a => a.value))}
                    onClick={() => {
                      resetForm();

                      const input = {
                        accountId: currentAccount,
                        ariFieldId: field.id,
                        dataType: field.dataType,
                        question: field.question,
                        answers: answers.map(a => a.value),
                        mandatory: field.mandatory,
                      };

                      updateLocalAri({ variables: { input } })
                        .then(() => {
                          onHide();
                          growl.show({
                            severity: 'success',
                            sticky: false,
                            summary: formatMessage(messages.growlAddFieldSuccessSummary),
                            detail: formatMessage(messages.growlEditAnswersSuccessDetail),
                          });
                        })
                        .catch(() => {
                          growl.show({
                            severity: 'error',
                            sticky: true,
                            summary: formatMessage(messages.growlAddFieldFailSummary),
                            detail: formatMessage(messages.growlEditAnswersFailDetail),
                          });
                        });
                    }}
                  />
                </div>
                <Debug />
              </Form>
            )}
          </Formik>
        </div>
      </ScrollPanel>
    </Dialog>
  );
};

EditAnswersDialog.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  isVisible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(withGrowl(EditAnswersDialog));
